import styled from '@emotion/styled'
import { useContext, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ApiClientConsentSetting } from '@storefronts/common-types'
import {
    BUTTON_BORDER_RADIUS_VAR,
    CustomizationContext,
    PRIMARY_CTA_FILL_COLOR_VAR,
    PRIMARY_CTA_TEXT_COLOR_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SECONDARY_FONT_CSS_VAR,
} from '../../context/CustomizationContextProvider'
import {
    IS_EDITOR_PREVIEW,
    QueryParamContext,
} from '../../context/QueryParamContextProvider'
import { useIsomorphicLayoutEffect } from '../../hooks/useIsomorphicLayoutEffect'
import { getTypographyRoleStyle } from '../../style/typography'
import { TYPOGRAPHY_SIZE } from '../../style/typographySizes'
import { scaledFontSize } from '../../style/scrollingLayout'
import {
    hasAcceptedConsent,
    isConsentModalEnabled,
    markConsentAccepted,
} from './consentTrackingStore'
import { InspectOverlayHost } from '../shared/InspectOverlayHost'

// --- Styled components ---

const Overlay = styled(InspectOverlayHost)({
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '16px',
})

const Card = styled.div({
    backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR}, #ffffff)`,
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR}, #000000)`,
    borderRadius: '16px',
    padding: '32px 24px',
    maxWidth: '400px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    textAlign: 'center',
    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
})

const Headline = styled.h2({
    margin: 0,
    ...getTypographyRoleStyle('headingText', {
        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
        fontWeight: 700,
        lineHeight: 1.2,
    }),
    fontSize: scaledFontSize(TYPOGRAPHY_SIZE.SECTION),
})

const SubHeadline = styled.p({
    margin: 0,
    fontSize: scaledFontSize(TYPOGRAPHY_SIZE.BODY),
    lineHeight: 1.5,
    opacity: 0.75,
})

const ButtonGroup = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    marginTop: '4px',
})

const AcceptButton = styled.button({
    backgroundColor: `var(${PRIMARY_CTA_FILL_COLOR_VAR}, #1a56ff)`,
    color: `var(${PRIMARY_CTA_TEXT_COLOR_VAR}, #ffffff)`,
    border: 'none',
    borderRadius: `var(${BUTTON_BORDER_RADIUS_VAR})`,
    padding: '14px 24px',
    fontSize: scaledFontSize(16),
    fontWeight: 600,
    cursor: 'pointer',
    width: '100%',
    ':focus-visible': {
        outline: `3px solid var(${PRIMARY_CTA_FILL_COLOR_VAR}, #1a56ff)`,
        outlineOffset: '2px',
    },
})

const DeclineText = styled.button({
    background: 'none',
    border: 'none',
    padding: '4px 0',
    fontSize: scaledFontSize(14),
    fontWeight: 500,
    cursor: 'pointer',
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR}, #000000)`,
    textDecoration: 'underline',
    ':focus-visible': {
        outline: `3px solid var(${PRIMARY_CTA_FILL_COLOR_VAR}, #1a56ff)`,
        outlineOffset: '2px',
    },
})

const FooterText = styled.p({
    margin: 0,
    fontSize: scaledFontSize(11),
    lineHeight: 1.5,
    opacity: 0.55,
    a: {
        color: 'inherit',
        textDecoration: 'underline',
    },
})

function resolveDisplayText(
    s: ApiClientConsentSetting | null | undefined,
    declined: boolean,
): { headline: string | null; subHeadline: string | null } {
    if (declined) {
        return {
            headline: s?.declineHeadline ?? 'Access denied',
            subHeadline: s?.declineSubHeadline ?? null,
        }
    }
    return {
        headline: s?.headline ?? null,
        subHeadline: s?.subHeadline ?? null,
    }
}

// --- Component ---

export const ConsentModal: React.FC = () => {
    const settings = useContext(CustomizationContext)
    const { IS_PREVIEW } = useContext(QueryParamContext)
    const consentSetting = settings.clientConsentSetting
    const isPreview = IS_PREVIEW || IS_EDITOR_PREVIEW()
    const consentEnabled = isConsentModalEnabled(consentSetting, isPreview)

    const [visible, setVisible] = useState(false)
    const [declined, setDeclined] = useState(false)
    const [portalTarget] = useState<Element | null>(() =>
        typeof document !== 'undefined'
            ? document.getElementById('modal-root')
            : null,
    )

    useIsomorphicLayoutEffect(() => {
        if (!consentEnabled) return
        if (!hasAcceptedConsent()) {
            setVisible(true)
        }
    }, [consentEnabled])

    useIsomorphicLayoutEffect(() => {
        if (!visible || !consentEnabled) return
        document.body.classList.add('scroll-locked')
        return () => {
            document.body.classList.remove('scroll-locked')
        }
    }, [visible, consentEnabled])

    if (!visible || !consentEnabled) return null

    const { headline, subHeadline } = resolveDisplayText(
        consentSetting,
        declined,
    )

    const handleAccept = () => {
        markConsentAccepted()
        setVisible(false)
    }

    const content = (
        <Overlay>
            <Card role="dialog" aria-modal="true">
                {headline && <Headline>{headline}</Headline>}
                {subHeadline && <SubHeadline>{subHeadline}</SubHeadline>}

                <ButtonGroup>
                    <AcceptButton onClick={handleAccept}>
                        {consentSetting?.acceptCtaText ?? 'Accept'}
                    </AcceptButton>
                    <DeclineText onClick={() => setDeclined(true)}>
                        {consentSetting?.declineCtaText ?? 'Decline'}
                    </DeclineText>
                </ButtonGroup>

                {consentSetting?.footerText && (
                    <FooterText
                        dangerouslySetInnerHTML={{
                            __html: consentSetting.footerText,
                        }}
                    />
                )}
            </Card>
        </Overlay>
    )

    return portalTarget ? createPortal(content, portalTarget) : content
}
